import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { AccountService } from '../account/account.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { BankingCredentialsService } from './banking-credentials.service';

type ProviderCanonical =
  | 'MT BANK'
  | 'BRX'
  | 'ONLY UP'
  | 'DIGITOPAY'
  | 'MW IP'
  | 'PAGSTARS'
  | 'IUGU';

interface ProviderExtraField {
  key:
  | 'clientId'
  | 'secretId'
  | 'holderId'
  | 'clientIdWithDraw'
  | 'secretIdWithDraw'
  | 'hmac';
  label: string;
  type?: 'text' | 'password';
}

interface WebhookDisplayItem {
  key: string;
  label: string;
  value: string;
}

interface ProviderTab {
  label: string;
  isReal: boolean;
}

@Component({
  selector: 'app-banking-credentials',
  templateUrl: './banking-credentials.component.html',
  styleUrls: ['./banking-credentials.component.scss']
})
export class BankingCredentialsComponent implements OnInit {
  @ViewChild('requestTOTPCode') requestTOTPCode!: TemplateRef<any>;

  public account: any;
  public accountTaxes: any;
  public selected = 0;

  public isLoaded = false;
  public isSaving = false;
  public validatingTotp = false;

  public validationCode = '';
  public errorMessage = '';

  public selectedProviderTab = 1;
  public providerTabs: { label: string; isReal: boolean }[] = [];

  public bankProvider: any = null;
  private originalBankProvider: any = null;

  private totpModalRef?: NgbModalRef;

  readonly allProvidersDisplay: string[] = [
    'MT Bank',
    'BRX',
    'Only Up',
    'DigitoPay',
    'MW Ip',
    'PagStars',
    'Iugu',
  ];

  readonly providerExtraFieldsMap: Record<ProviderCanonical, ProviderExtraField[]> = {
    'MT BANK': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
      { key: 'holderId', label: 'Holder ID' },
    ],
    'BRX': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
      { key: 'holderId', label: 'Holder ID' },
    ],
    'ONLY UP': [
      { key: 'clientId', label: 'Client ID (Depósito)' },
      { key: 'secretId', label: 'Secret ID (Depósito)' },
      { key: 'clientIdWithDraw', label: 'Client ID (Saque)' },
      { key: 'secretIdWithDraw', label: 'Secret ID (Saque)' },
    ],
    'DIGITOPAY': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
    ],
    'MW IP': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
      { key: 'hmac', label: 'Hmac' },
    ],
    'PAGSTARS': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
    ],
    'IUGU': [
      { key: 'clientId', label: 'Client ID' },
      { key: 'secretId', label: 'Secret ID' },
    ],
  };

  readonly webhookLabels: Record<string, string> = {
    depositQrcode: 'Depósito QRCode',
    depositPixKey: 'Depósito Chave Pix',
    withdraw: 'Saque',
    refund: 'Reembolso',
    return: 'Retorno',
    medpix: 'MedPix',
  };

  form = new FormGroup({
    withdrawTaxValue: new FormControl<any>(0, { nonNullable: true }),
    withdrawTaxPercent: new FormControl<number>(0, { nonNullable: true }),
    depositTaxValue: new FormControl<any>(0, { nonNullable: true }),
    depositTaxPercent: new FormControl<number>(0, { nonNullable: true }),
    pixKey: new FormControl<string>('', { nonNullable: true }),
    saQAG: new FormControl<string>('', { nonNullable: true }),
    saQCC: new FormControl<string>('', { nonNullable: true }),

    // dinâmico por provedor
    clientId: new FormControl<string>('', { nonNullable: true }),
    secretId: new FormControl<string>('', { nonNullable: true }),
    holderId: new FormControl<string>('', { nonNullable: true }),
    clientIdWithDraw: new FormControl<string>('', { nonNullable: true }),
    secretIdWithDraw: new FormControl<string>('', { nonNullable: true }),
    hmac: new FormControl<string>('', { nonNullable: true }),
  });

  constructor(
    private accountService: AccountService,
    private authService: AuthService,
    private bankingCredentialsService: BankingCredentialsService,
    private toastr: ToastrService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.loadPage();
  }

  async loadPage(): Promise<void> {
    this.isLoaded = false;

    try {
      const [account, accountTaxes, bankProvider] = await Promise.all([
        this.accountService.getAccount(),
        this.accountService.getAccountTaxes(),
        this.bankingCredentialsService.getBankProvider(),
      ]);

      this.account = account;
      this.accountTaxes = accountTaxes;
      this.selected = this.account?.personType == 0 ? 1 : 2;

      this.bankProvider = bankProvider;
      this.originalBankProvider = this.deepClone(bankProvider);

      this.patchFormFromBankProvider(bankProvider);

      this.selectedProviderTab = 1;

      this.buildProviderTabs();
      this.selectedProviderTab = 1;
    } catch (err) {
      console.error(err);
      this.toastr.error('Erro ao carregar credenciais bancárias.', 'Erro', {
        timeOut: 3000,
        positionClass: 'toast-top-right'
      });
    } finally {
      this.isLoaded = true;
    }
  }

  get providerTitle(): string {
    return this.bankProvider?.name || this.bankProvider?.bankName || 'Bank Provider';
  }

  get providerExtraFields(): ProviderExtraField[] {
    const canonical = this.currentProviderCanonical;
    if (!canonical) return [];
    return this.providerExtraFieldsMap[canonical] ?? [];
  }

  private replaceGuidBankInWebhook(url: string): string {
    const value = (url ?? '').toString().trim();
    const providerId = (this.bankProvider?.id ?? '').toString().trim();

    if (!value || !providerId) {
      return value;
    }

    return value.replace(/\/guidbank(?=\/|$)/gi, `/${providerId}`);
  }

  private get currentProviderCanonical(): ProviderCanonical | null {
    const baseName = this.bankProvider?.name || this.providerTitle;
    const normalized = this.normalizeProviderName(baseName);

    switch (normalized) {
      case 'mt bank':
        return 'MT BANK';
      case 'brx':
        return 'BRX';
      case 'only up':
        return 'ONLY UP';
      case 'digitopay':
        return 'DIGITOPAY';
      case 'mw ip':
        return 'MW IP';
      case 'pagstars':
        return 'PAGSTARS';
      case 'iugu':
        return 'IUGU';
      default:
        return null;
    }
  }

  private getCurrentProviderDisplayLabel(): string {
    const baseName = this.bankProvider?.name || this.providerTitle;
    const normalized = this.normalizeProviderName(baseName);
    const found = this.allProvidersDisplay.find(p => this.normalizeProviderName(p) === normalized);
    return found || baseName || 'Bank Provider';
  }

  private normalizeProviderName(name: string): string {
    const raw = (name || '').trim().toLowerCase();

    if (raw === 'mtbank') return 'mt bank';
    if (raw === 'onlyup') return 'only up';
    if (raw === 'digito pay') return 'digitopay';
    if (raw === 'pag stars') return 'pagstars';

    return raw;
  }

  get visibleWebhooks(): WebhookDisplayItem[] {
    const raw = this.bankProvider?.webHookProvedor;

    if (raw && !Array.isArray(raw) && typeof raw === 'object') {
      return Object.keys(this.webhookLabels)
        .map((key) => ({
          key,
          label: this.webhookLabels[key],
          value: this.replaceGuidBankInWebhook(
            (raw[key] ?? '').toString().trim()
          ),
        }))
        .filter((item) => !!item.value);
    }

    if (Array.isArray(raw)) {
      const normalizedMap: Record<string, string> = {};

      for (const item of raw) {
        const key = (item?.type || item?.name || item?.key || '')
          .toString()
          .trim();

        const value = this.replaceGuidBankInWebhook(
          (item?.url || item?.value || item?.webHook || '')
            .toString()
            .trim()
        );

        if (key && value) {
          normalizedMap[key] = value;
        }
      }

      return Object.keys(this.webhookLabels)
        .map((key) => ({
          key,
          label: this.webhookLabels[key],
          value: (normalizedMap[key] ?? '').trim(),
        }))
        .filter((item) => !!item.value);
    }

    return [];
  }


  private patchFormFromBankProvider(bp: any): void {
    const withdraw = bp?.withDrawConfiguration ?? {};
    const deposit = bp?.depositConfiguration ?? {};

    const pixKey = (deposit?.pixKey ?? withdraw?.pixKey ?? '') || '';

    this.form.patchValue(
      {
        withdrawTaxValue: this.toNumber(withdraw?.taxValue),
        withdrawTaxPercent: this.toNumber(withdraw?.taxPercent),
        depositTaxValue: this.toNumber(deposit?.taxValue),
        depositTaxPercent: this.toNumber(deposit?.taxPercent),

        pixKey: (pixKey ?? '').toString(),
        saQAG: (bp?.saQAG ?? '').toString(),
        saQCC: (bp?.saQCC ?? '').toString(),

        clientId: (bp?.clientId ?? '').toString(),
        secretId: (bp?.secretId ?? '').toString(),
        holderId: (bp?.holderId ?? '').toString(),
        clientIdWithDraw: (bp?.clientIdWithDraw ?? '').toString(),
        secretIdWithDraw: (bp?.secretIdWithDraw ?? '').toString(),
        hmac: (bp?.hmac ?? '').toString(),
      },
      { emitEvent: false }
    );
  }

  private buildUpdatePayload(): any {
    const payload = this.deepClone(this.originalBankProvider ?? {});
    const v = this.form.getRawValue();

    payload.withDrawConfiguration = payload.withDrawConfiguration ?? {};
    payload.depositConfiguration = payload.depositConfiguration ?? {};

    payload.withDrawConfiguration.taxValue = this.toNumber(v.withdrawTaxValue);
    payload.withDrawConfiguration.taxPercent = this.toNumber(v.withdrawTaxPercent);

    payload.depositConfiguration.taxValue = this.toNumber(v.depositTaxValue);
    payload.depositConfiguration.taxPercent = this.toNumber(v.depositTaxPercent);

    payload.withDrawConfiguration.pixKey = (v.pixKey ?? '').trim() || null;
    payload.depositConfiguration.pixKey = (v.pixKey ?? '').trim() || null;

    payload.saQAG = (v.saQAG ?? '').trim() || null;
    payload.saQCC = (v.saQCC ?? '').trim() || null;

    payload.clientId = (v.clientId ?? '').trim() || null;
    payload.secretId = (v.secretId ?? '').trim() || null;
    payload.holderId = (v.holderId ?? '').trim() || null;
    payload.clientIdWithDraw = (v.clientIdWithDraw ?? '').trim() || null;
    payload.secretIdWithDraw = (v.secretIdWithDraw ?? '').trim() || null;
    payload.hmac = (v.hmac ?? '').trim() || null;

    return payload;
  }


  copyId(text: string): void {
    if (!text) {
      this.toastr.warning('Não há nada para copiar', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
      return;
    }

    navigator.clipboard.writeText(text).then(() => {
      this.toastr.success('Copiado com sucesso!', 'Sucesso', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    }).catch(() => {
      this.toastr.error('Erro ao copiar', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    });
  }

  save(): void {
    if (this.isSaving || this.validatingTotp) return;

    if (!this.bankProvider) {
      this.toastr.warning('Bank Provider não carregado.', 'Aviso', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
      return;
    }

    if (!this.validateBusinessRules()) {
      return;
    }

    this.errorMessage = '';
    this.validationCode = '';

    this.totpModalRef = this.modalService.open(this.requestTOTPCode, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  async paste(controlName: string): Promise<void> {
    try {
      const text = (await navigator.clipboard.readText()).trim();

      const control = this.form.get(controlName);
      if (!control) {
        this.toastr.warning('Campo não encontrado para colar.', 'Aviso', {
          timeOut: 2000,
          positionClass: 'toast-top-right'
        });
        return;
      }

      control.setValue(text);
    } catch (err) {
      console.error('Erro ao colar conteúdo da área de transferência:', err);
      this.toastr.error('Erro ao colar conteúdo.', 'Erro', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    }
  }

  async pasteTotpCode(): Promise<void> {
    try {
      this.validationCode = (await navigator.clipboard.readText()).trim();
    } catch (err) {
      console.error('Erro ao colar token:', err);
    }
  }

  async isValidTotp(): Promise<boolean> {
    try {
      const res = await this.authService.validateTotpCode(this.validationCode);
      return !!res?.isValid;
    } catch {
      return false;
    }
  }

  async validateTotp(): Promise<void> {
    if (this.validatingTotp || this.isSaving) return;

    this.validatingTotp = true;
    this.errorMessage = '';

    const ok = await this.isValidTotp();
    if (!ok) {
      this.errorMessage = 'Código de autenticação inválido!';
      this.validatingTotp = false;
      return;
    }

    this.isSaving = true;
    const payload = this.buildUpdatePayload();

    this.bankingCredentialsService.updateBankProvider(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.validatingTotp = false;
        })
      )
      .subscribe({
        next: () => {
          this.totpModalRef?.close();

          this.toastr.success('Credenciais bancárias atualizadas com sucesso!', 'Sucesso', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });

          this.loadPage();
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            err?.error?.mensagem || 'Erro ao atualizar credenciais bancárias.',
            'Erro',
            {
              timeOut: 3000,
              positionClass: 'toast-top-right',
            }
          );
        }
      });
  }


  private validateBusinessRules(): boolean {
    const v = this.form.getRawValue();

    const withdrawTaxValue = this.toNumber(v.withdrawTaxValue);
    const depositTaxValue = this.toNumber(v.depositTaxValue);
    const withdrawTaxPercent = this.toNumber(v.withdrawTaxPercent);
    const depositTaxPercent = this.toNumber(v.depositTaxPercent);

    if (withdrawTaxValue < 0 || depositTaxValue < 0) {
      this.toastr.warning('Taxas fixas não podem ser negativas.', 'Aviso', {
        timeOut: 2500,
        positionClass: 'toast-top-right'
      });
      return false;
    }

    if (withdrawTaxPercent < 0 || depositTaxPercent < 0) {
      this.toastr.warning('Taxas percentuais não podem ser negativas.', 'Aviso', {
        timeOut: 2500,
        positionClass: 'toast-top-right'
      });
      return false;
    }

    return true;
  }


  private toNumber(v: any): number {
    if (typeof v === 'number') {
      return Number.isFinite(v) ? v : 0;
    }

    if (typeof v === 'string') {
      const normalized = v
        .replace(/\s/g, '')
        .replace('R$', '')
        .replace(/\./g, '')
        .replace(',', '.');

      const n = Number(normalized);
      return Number.isFinite(n) ? n : 0;
    }

    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  private deepClone<T>(obj: T): T {
    try {
      // @ts-ignore
      if (typeof structuredClone === 'function') {
        return structuredClone(obj);
      }
    } catch { }
    return JSON.parse(JSON.stringify(obj));
  }

  private buildProviderTabs(): void {
    const currentLabel = this.getCurrentProviderDisplayLabel();
    const currentNormalized = this.normalizeProviderName(currentLabel);

    const fakeTabs = this.allProvidersDisplay
      .filter(p => this.normalizeProviderName(p) !== currentNormalized)
      .map(label => ({ label, isReal: false }));

    this.providerTabs = [{ label: currentLabel, isReal: true }, ...fakeTabs];
  }
}